// EPIC-P05-07: Badge Filter Bar Component
// Search and filter controls for badge list

import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Search, X } from 'lucide-react'
import { AdminCard } from '../primitives'

export interface BadgeFilters {
  search: string
  category: string
  rarity: string
  is_active: string
  sort_by: string
  sort_order: 'asc' | 'desc'
}

interface BadgeFilterBarProps {
  filters: BadgeFilters
  onFiltersChange: (filters: Partial<BadgeFilters>) => void
}

export function BadgeFilterBar({ filters, onFiltersChange }: BadgeFilterBarProps) {
  const handleSearchChange = (value: string) => {
    onFiltersChange({ search: value })
  }

  const handleFilterChange = (key: keyof BadgeFilters, value: string) => {
    onFiltersChange({ [key]: value })
  }

  const clearFilters = () => {
    onFiltersChange({
      search: '',
      category: '',
      rarity: '',
      is_active: '',
      sort_by: 'updated_at',
      sort_order: 'desc',
    })
  }

  const hasActiveFilters = filters.search || filters.category || filters.rarity || filters.is_active !== ''

  return (
    <AdminCard accentColor="info">
      <div className="space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, key, or description..."
            value={filters.search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Filters Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Category */}
          <Select value={filters.category || 'all'} onValueChange={(v) => handleFilterChange('category', v === 'all' ? '' : v)}>
            <SelectTrigger>
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="analysis">Analysis</SelectItem>
              <SelectItem value="practice">Practice</SelectItem>
              <SelectItem value="streak">Streak</SelectItem>
              <SelectItem value="milestone">Milestone</SelectItem>
              <SelectItem value="social">Social</SelectItem>
            </SelectContent>
          </Select>

          {/* Rarity */}
          <Select value={filters.rarity || 'all'} onValueChange={(v) => handleFilterChange('rarity', v === 'all' ? '' : v)}>
            <SelectTrigger>
              <SelectValue placeholder="All Rarities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Rarities</SelectItem>
              <SelectItem value="common">Common</SelectItem>
              <SelectItem value="rare">Rare</SelectItem>
              <SelectItem value="epic">Epic</SelectItem>
              <SelectItem value="legendary">Legendary</SelectItem>
            </SelectContent>
          </Select>

          {/* Active Status */}
          <Select value={filters.is_active || 'all'} onValueChange={(v) => handleFilterChange('is_active', v === 'all' ? '' : v)}>
            <SelectTrigger>
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="true">Active</SelectItem>
              <SelectItem value="false">Inactive</SelectItem>
            </SelectContent>
          </Select>

          {/* Sort */}
          <Select value={filters.sort_by} onValueChange={(v) => handleFilterChange('sort_by', v)}>
            <SelectTrigger>
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="updated_at">Last Updated</SelectItem>
              <SelectItem value="title">Name</SelectItem>
              <SelectItem value="rarity">Rarity</SelectItem>
              <SelectItem value="created_at">Created Date</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Sort Order & Clear */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Sort:</span>
            <Select value={filters.sort_order} onValueChange={(v) => handleFilterChange('sort_order', v as 'asc' | 'desc')}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="desc">Descending</SelectItem>
                <SelectItem value="asc">Ascending</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {hasActiveFilters && (
            <Button variant="outline" size="sm" onClick={clearFilters}>
              <X className="h-4 w-4 mr-2" />
              Clear Filters
            </Button>
          )}
        </div>
      </div>
    </AdminCard>
  )
}

